// components/analytics/components/ExportModal.tsx
import React, { useState } from "react";
import { Modal, Form, DatePicker, Select, Radio, Button, message } from "antd";
import { ANALYTICS_API_URL } from "../../../constants/config";
import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useTranslation } from "react-i18next";
import { getAndSanitize } from "../../../shared/utils/sanitization";

dayjs.extend(utc);
dayjs.extend(timezone);

const { RangePicker } = DatePicker;

interface ExportModalProps {
  visible: boolean;
  onCancel: () => void;
  accountNames: string[];
}

const ExportModal: React.FC<ExportModalProps> = ({
  visible,
  onCancel,
  accountNames,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const handleExport = async (values: any) => {
    try {
      setLoading(true);

      const { dateRange, accountName, format } = values;

      // Build query params
      const params: Record<string, string> = {
        format: format || "json",
      };

      // Add date range if provided
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].toISOString();
        params.endDate = dateRange[1].toISOString();
      }

      // Add account if provided
      if (accountName) {
        params.accountName = accountName;
      }

      // Make request to export endpoint
      const response = await axios.get(`${ANALYTICS_API_URL}/api/analytics/export`, {
        headers: {
          Authorization: `Bearer ${getAndSanitize("token")}`,
          "X-Admin-ID": getAndSanitize("adminId") || "",
        },
        params,
        responseType: "blob",
      });

      // Create file name based on date and format
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10);
      const fileName = `analytics-export-${dateStr}.${
        format === "xlsx" ? "xlsx" : format
      }`;

      // Download the file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Close modal
      message.success(t("analytics.ExportModal.messages.success"));
      onCancel();
    } catch (error) {
      console.error("Export error:", error);
      message.error(t("analytics.ExportModal.messages.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t("analytics.ExportModal.title")}
      visible={visible}
      footer={null}
      onCancel={onCancel}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleExport}
        initialValues={{
          format: "json",
          dateRange: [dayjs().subtract(30, "days"), dayjs()],
        }}
      >
        <Form.Item
          name="dateRange"
          label={t("analytics.ExportModal.form.dateRange.label")}
          rules={[{ required: true, message: t("analytics.ExportModal.form.dateRange.required") }]}
        >
          <RangePicker style={{ width: "100%" }} showTime />
        </Form.Item>

        <Form.Item name="accountName" label={t("analytics.ExportModal.form.accountName.label")}>
          <Select
            allowClear
            placeholder={t("analytics.ExportModal.form.accountName.placeholder")}
            showSearch
            filterOption={(input, option) =>
              (option?.children as unknown as string)
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
          >
            {accountNames.map((name) => (
              <Select.Option key={name} value={name}>
                {name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="format"
          label={t("analytics.ExportModal.form.format.label")}
          rules={[{ required: true, message: t("analytics.ExportModal.form.format.required") }]}
        >
          <Radio.Group>
            <Radio.Button value="json">JSON</Radio.Button>
            <Radio.Button value="csv">CSV</Radio.Button>
            <Radio.Button value="xlsx">Excel</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            style={{ marginRight: 16 }}
          >
            {t("analytics.ExportModal.buttons.export")}
          </Button>
          <Button onClick={onCancel}>{t("analytics.ExportModal.buttons.cancel")}</Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ExportModal;
